import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import type { paths } from "@/api/types";
import type { Result } from "@/shared/api";
import { apiGet, apiGetPage, nextCursor, PAGE_LIMIT, qk } from "@/shared/api";

export type UserDetail = Result<paths["/api/v1/admin/users/{user_id}"]["get"]>;
export type UserBrief = Result<paths["/api/v1/admin/users"]["get"]>[number];
export type UserTransaction =
  Result<paths["/api/v1/admin/users/{user_id}/transactions"]["get"]>[number];

/**
 * Список пользователей. Курсорная страница, а не offset: список растёт, и
 * offset давал бы дубли между страницами.
 */
export function useUsers(query: string, blocked: boolean | null) {
  return useInfiniteQuery({
    queryKey: qk.users(query, blocked),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGetPage("/api/v1/admin/users", {
        query: {
          limit: PAGE_LIMIT,
          ...(query ? { q: query } : {}),
          ...(blocked === null ? {} : { blocked }),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
        signal,
      }),
    getNextPageParam: (last) => nextCursor(last.meta),
  });
}

/**
 * Карточка пользователя.
 *
 * `enabled` нужен экрану начислений: пока пользователь не выбран, id там нет,
 * и без флага запрос уходил бы на `/users/0` и возвращал 422.
 */
export function useUser(id: number, enabled = true) {
  return useQuery({
    queryKey: qk.user(id),
    enabled,
    queryFn: ({ signal }) =>
      apiGet("/api/v1/admin/users/{user_id}", { params: { user_id: id }, signal }),
  });
}

export function useUserTransactions(id: number) {
  return useInfiniteQuery({
    queryKey: qk.userTransactions(id),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) =>
      apiGetPage("/api/v1/admin/users/{user_id}/transactions", {
        params: { user_id: id },
        query: { limit: PAGE_LIMIT, ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      }),
    getNextPageParam: (last) => nextCursor(last.meta),
  });
}
